import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ApiError, type RoleType } from "@mydailydirect/api-client";
import {
  AuthProvider,
  useAuth,
  ALL_ROLES,
  SUPER_ADMIN_ROLES,
  isSuperAdminPhone,
  PRODUCTION_USERS,
  registerUserRecord,
  normalizePhone,
  sendFirebasePhoneOTP,
  verifyFirebasePhoneOTP,
  findLocalVendorByPhone,
  type Session,
  type UserRecord,
  type ConfirmationResult,
} from "@mydailydirect/auth";
import { LoginScreen, ThemeProvider, ToastProvider, useToast, applyDesignDirection, readDesignDirection, type DesignDirection } from "@mydailydirect/ui";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { track } from "./telemetry";

export interface AppRootProps {
  appName: string;
  subtitle?: string;
  roleLabel: string;
  /** Roles this app authenticates as; stamped into the minted assertion at login. */
  roles: RoleType[];
  /** Default portal design system direction (e.g. "navy" for Catalog, "graphite" for Admin, "mandi" for Vendor/RM). */
  designDirection?: DesignDirection;
  children: ReactNode;
}

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: (count, error) => {
          // Don't retry auth/validation/not-found — only transient/network/server errors.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return count < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

function LoginGate({ appName, subtitle, roleLabel, roles, children }: AppRootProps) {
  const { session, login, logout, register, client } = useAuth();
  const { toast } = useToast();
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const isRetailerPortal = roles.includes("RETAILER");
  const isVendorPortal = roles.includes("VENDOR");

  // A session is valid for this portal if it carries one of this portal's roles OR is an Overall ADMIN / SUPER_ADMIN.
  const authorized =
    !!session && session.roles.some((r) => r === "ADMIN" || r === "SUPER_ADMIN" || roles.includes(r));

  useEffect(() => {
    if (session && !authorized) {
      logout();
      return;
    }
    if (!session) return;

    // Verify active session against backend PostgreSQL database so stale local sessions
    // (e.g. a Retailer account previously cached on the Admin portal) are immediately invalidated.
    let cancelled = false;
    (async () => {
      try {
        const digits = normalizePhone(session.phone || session.userId);
        const backendUsers = await client.users.list();
        if (cancelled) return;

        const matchingUsers = backendUsers.filter(
          (u) =>
            u.username.toLowerCase() === session.userId.toLowerCase() ||
            (digits.length > 0 && normalizePhone(u.phone) === digits) ||
            (digits.length > 0 && normalizePhone(u.username) === digits),
        );

        const isBackendAdmin =
          matchingUsers.some(
            (u) => (u.role === "ADMIN" || u.role === "SUPER_ADMIN") && u.accountStatus !== "DISABLED",
          ) ||
          PRODUCTION_USERS.some(
            (p) =>
              (p.userId.toLowerCase() === session.userId.toLowerCase() ||
                (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
              (p.roles.includes("ADMIN") || p.roles.includes("SUPER_ADMIN")),
          );

        if (isBackendAdmin) {
          const resolvedPhone =
            session.phone || session.phoneNumber || matchingUsers[0]?.phone || session.userId;
          const isCoFounderSuperAdmin = isSuperAdminPhone(resolvedPhone) || isSuperAdminPhone(session.userId);
          const targetRoles: RoleType[] = isCoFounderSuperAdmin ? [...SUPER_ADMIN_ROLES] : [...ALL_ROLES];

          if (
            session.tenantId !== "mydailydirect" ||
            targetRoles.some((r) => !session.roles.includes(r)) ||
            (!isCoFounderSuperAdmin && session.roles.includes("SUPER_ADMIN")) ||
            !session.orgId ||
            !session.phone
          ) {
            login({
              ...session,
              tenantId: "mydailydirect",
              roles: targetRoles,
              phone: resolvedPhone,
              phoneNumber: resolvedPhone,
              orgId: session.orgId || session.organizationId || "org_mdd_admin",
              organizationId: session.orgId || session.organizationId || "org_mdd_admin",
            });
          }
          return;
        }

        if (isRetailerPortal && matchingUsers.length > 0) {
          const backendName = matchingUsers[0].fullName?.trim();
          if (
            backendName &&
            (!session.businessName ||
              session.businessName === "Buyer" ||
              session.businessName.startsWith("Store ") ||
              session.fullName !== backendName ||
              !session.orgId ||
              !session.phone)
          ) {
            login({
              ...session,
              fullName: backendName,
              phone: session.phone || session.phoneNumber || matchingUsers[0]?.phone || undefined,
              phoneNumber: session.phone || session.phoneNumber || matchingUsers[0]?.phone || undefined,
              orgId: session.orgId || session.organizationId || (matchingUsers[0]?.retailerId ? `retailer_${matchingUsers[0].retailerId}` : "mydailydirect"),
              organizationId: session.orgId || session.organizationId || (matchingUsers[0]?.retailerId ? `retailer_${matchingUsers[0].retailerId}` : "mydailydirect"),
              businessName: session.businessName && !session.businessName.startsWith("Store ")
                ? session.businessName
                : backendName,
            });
          }
        }

        if (!isRetailerPortal && !isVendorPortal) {
          // On Operations portals (Admin, Trader, RM, Driver), require an active matching role
          const hasValidBackendRole = matchingUsers.some(
            (u) => roles.includes(u.role) && u.accountStatus !== "DISABLED",
          );
          const hasPreseededRole = PRODUCTION_USERS.some(
            (p) =>
              (p.userId.toLowerCase() === session.userId.toLowerCase() ||
                (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
              p.roles.some((r) => roles.includes(r)),
          );
          if (!hasValidBackendRole && !hasPreseededRole) {
            logout();
          }
        }
      } catch {
        // Keep session if offline
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, authorized, isRetailerPortal, roles, client, login, logout]);

  async function resolvePortalAccess(lookupKey: string): Promise<UserRecord | null> {
    const query = lookupKey.trim().toLowerCase();
    const digits = normalizePhone(lookupKey);

    try {
      const backendUsers = await client.users.list();
      const matchedBackendUsers = backendUsers.filter(
        (u) =>
          u.username.toLowerCase() === query ||
          (digits.length > 0 && normalizePhone(u.phone) === digits) ||
          (digits.length > 0 && normalizePhone(u.username) === digits),
      );

      // 1. Overall ADMIN / SUPER_ADMIN accounts have access across all portals; only Co-Founders (+919962233577 & +917075678910) get SUPER_ADMIN
      const adminBackendUser = matchedBackendUsers.find(
        (u) => (u.role === "ADMIN" || u.role === "SUPER_ADMIN") && u.accountStatus !== "DISABLED",
      );
      const adminPreseededUser = PRODUCTION_USERS.find(
        (p) =>
          (p.userId.toLowerCase() === query ||
            (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
          (p.roles.includes("ADMIN") || p.roles.includes("SUPER_ADMIN")),
      );
      if (adminBackendUser || adminPreseededUser) {
        const resolvedPhone =
          adminBackendUser?.phone ?? adminPreseededUser?.phone ?? `+91 ${digits}`;
        const isCoFounder =
          isSuperAdminPhone(resolvedPhone) ||
          isSuperAdminPhone(adminBackendUser?.username) ||
          isSuperAdminPhone(adminPreseededUser?.userId);
        return registerUserRecord({
          userId: adminBackendUser?.username ?? adminPreseededUser!.userId,
          passwordHash: "otp_verified",
          fullName:
            adminBackendUser?.fullName ??
            adminPreseededUser?.fullName ??
            (isCoFounder ? "Co-Founder Super Admin" : "Overall Administrator"),
          roles: isCoFounder ? [...SUPER_ADMIN_ROLES] : [...ALL_ROLES],
          phone: resolvedPhone,
          businessName: adminPreseededUser?.businessName ?? "My Daily Direct HQ",
          accountType: isCoFounder ? "Co-Founder Super Admin" : roleLabel,
        });
      }

      // 2. Retailer Store Portal: allow any active registered user OR return null for new Retailer self-registration
      if (isRetailerPortal) {
        const activeUserMatch =
          matchedBackendUsers.find(
            (u) => u.role === "RETAILER" && u.accountStatus !== "DISABLED",
          ) ?? matchedBackendUsers.find((u) => u.accountStatus !== "DISABLED");

        if (activeUserMatch) {
          let resolvedShopName = activeUserMatch.fullName || activeUserMatch.username;
          if (activeUserMatch.retailerId) {
            try {
              const r = await client.retailers.get(activeUserMatch.retailerId);
              if (r?.name) resolvedShopName = r.name;
            } catch {
              // Fallback to user fullName
            }
          }
          const userPhone = activeUserMatch.phone || `+91 ${digits}`;
          const userOrgId = activeUserMatch.retailerId
            ? `retailer_${activeUserMatch.retailerId}`
            : activeUserMatch.vendorId
            ? `vendor_${activeUserMatch.vendorId}`
            : activeUserMatch.role === "ADMIN"
            ? "org_mdd_admin"
            : "mydailydirect";

          return registerUserRecord({
            userId: activeUserMatch.username,
            passwordHash: "otp_verified",
            fullName: activeUserMatch.fullName || resolvedShopName,
            roles: Array.from(new Set([activeUserMatch.role, "RETAILER"])),
            phone: userPhone,
            phoneNumber: userPhone,
            orgId: userOrgId,
            organizationId: userOrgId,
            businessName: resolvedShopName,
            accountType: roleLabel,
          });
        }

        if (digits.length > 0) {
          const backendRetailers = await client.retailers.list();
          const matchedRetailer = backendRetailers.find((r) => normalizePhone(r.phone) === digits);
          if (matchedRetailer) {
            const retailerPhone = matchedRetailer.phone || `+91 ${digits}`;
            const retailerOrgId = matchedRetailer.id ? `retailer_${matchedRetailer.id}` : "mydailydirect";
            return registerUserRecord({
              userId: matchedRetailer.phone || lookupKey,
              passwordHash: "otp_verified",
              fullName: matchedRetailer.name,
              roles: ["RETAILER"],
              phone: retailerPhone,
              phoneNumber: retailerPhone,
              orgId: retailerOrgId,
              organizationId: retailerOrgId,
              businessName: matchedRetailer.name,
              city: matchedRetailer.city || "Bengaluru",
              gstNumber: matchedRetailer.gstNumber || undefined,
              accountType: roleLabel,
            });
          }
        }

        // Not registered as a Retailer yet — allow self-serve registration on Retailer Store
        return null;
      }

      // 3. Vendor Portal: pre-seeded vendor, matching vendor list, or self-serve vendor demo
      if (isVendorPortal) {
        const preseededVendor = PRODUCTION_USERS.find(
          (p) =>
            (p.userId.toLowerCase() === query ||
              (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
            p.roles.some((r) => r === "VENDOR" || r === "ADMIN"),
        );
        if (preseededVendor) {
          return registerUserRecord(preseededVendor);
        }

        const vendorBackendUser = matchedBackendUsers.find(
          (u) => (u.role === "VENDOR" || roles.includes(u.role)) && u.accountStatus !== "DISABLED",
        );
        if (vendorBackendUser) {
          const vPhone = vendorBackendUser.phone || `+91 ${digits}`;
          return registerUserRecord({
            userId: vendorBackendUser.username,
            passwordHash: "otp_verified",
            fullName: vendorBackendUser.fullName || vendorBackendUser.username,
            roles: ["VENDOR"],
            phone: vPhone,
            phoneNumber: vPhone,
            orgId: vendorBackendUser.vendorId ? `vendor_${vendorBackendUser.vendorId}` : "mydailydirect",
            organizationId: vendorBackendUser.vendorId ? `vendor_${vendorBackendUser.vendorId}` : "mydailydirect",
            businessName: vendorBackendUser.fullName || "Verified Wholesale Supplier",
            accountType: roleLabel,
          });
        }

        const localVendor = findLocalVendorByPhone(digits || lookupKey);
        if (localVendor) {
          const vPhone = localVendor.phone || `+91 ${digits}`;
          return registerUserRecord({
            userId: localVendor.phone || lookupKey,
            passwordHash: "otp_verified",
            fullName: localVendor.contactName || localVendor.tradeName || localVendor.legalName,
            roles: ["VENDOR"],
            phone: vPhone,
            phoneNumber: vPhone,
            orgId: localVendor.id ? `vendor_${localVendor.id}` : "mydailydirect",
            organizationId: localVendor.id ? `vendor_${localVendor.id}` : "mydailydirect",
            businessName: localVendor.tradeName || localVendor.legalName,
            accountType: roleLabel,
          });
        }

        const cleanDigits = digits || normalizePhone(lookupKey);
        const vendorName = cleanDigits ? `Verified Supplier (${cleanDigits.slice(-4)})` : "Verified Wholesale Supplier";
        return registerUserRecord({
          userId: cleanDigits || lookupKey,
          passwordHash: "otp_verified",
          fullName: vendorName,
          roles: ["VENDOR"],
          phone: cleanDigits ? `+91 ${cleanDigits}` : lookupKey,
          phoneNumber: cleanDigits ? `+91 ${cleanDigits}` : lookupKey,
          orgId: `vendor_${cleanDigits || "demo"}`,
          organizationId: `vendor_${cleanDigits || "demo"}`,
          businessName: vendorName,
          accountType: roleLabel,
        });
      }

      // 4. Operations Portals (ADMIN, TRADER, RM, DRIVER): must be explicitly authorized for this portal's role
      const authorizedBackendUser = matchedBackendUsers.find(
        (u) => roles.includes(u.role) && u.accountStatus !== "DISABLED",
      );
      if (authorizedBackendUser) {
        const opPhone = authorizedBackendUser.phone || `+91 ${digits}`;
        const opOrgId = authorizedBackendUser.retailerId
          ? `retailer_${authorizedBackendUser.retailerId}`
          : authorizedBackendUser.vendorId
          ? `vendor_${authorizedBackendUser.vendorId}`
          : authorizedBackendUser.role === "ADMIN"
          ? "org_mdd_admin"
          : "mydailydirect";

        return registerUserRecord({
          userId: authorizedBackendUser.username,
          passwordHash: "otp_verified",
          fullName: authorizedBackendUser.fullName || authorizedBackendUser.username,
          roles: [authorizedBackendUser.role],
          phone: opPhone,
          phoneNumber: opPhone,
          orgId: opOrgId,
          organizationId: opOrgId,
          accountType: roleLabel,
        });
      }

      const preseededMatch = PRODUCTION_USERS.find(
        (p) =>
          (p.userId.toLowerCase() === query ||
            (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
          p.roles.some((r) => roles.includes(r)),
      );
      if (preseededMatch) {
        return registerUserRecord(preseededMatch);
      }

      throw new Error(
        `Access Denied: Mobile number +91 ${digits || lookupKey} is not authorized for the ${roleLabel} Portal. ${roleLabel} accounts must be added by an Administrator in Team Access before logging in.`,
      );
    } catch (err) {
      if (err instanceof Error && err.message.startsWith("Access Denied:")) {
        throw err;
      }
      // Network fallback to pre-seeded PRODUCTION_USERS
      const fallbackAdmin = PRODUCTION_USERS.find(
        (p) =>
          (p.userId.toLowerCase() === query ||
            (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
          (p.roles.includes("ADMIN") || p.roles.includes("SUPER_ADMIN")),
      );
      if (fallbackAdmin) {
        const isCoFounder = isSuperAdminPhone(fallbackAdmin.phone) || isSuperAdminPhone(fallbackAdmin.userId);
        return {
          ...fallbackAdmin,
          roles: isCoFounder ? [...SUPER_ADMIN_ROLES] : [...ALL_ROLES],
        };
      }
      const fallbackMatch = PRODUCTION_USERS.find(
        (p) =>
          (p.userId.toLowerCase() === query ||
            (digits.length > 0 && normalizePhone(p.phone) === digits)) &&
          p.roles.some((r) => roles.includes(r)),
      );
      if (fallbackMatch && !isRetailerPortal) {
        return fallbackMatch;
      }
      if (isRetailerPortal) {
        return null;
      }
      throw new Error(
        `Access Denied: Mobile number +91 ${digits || lookupKey} is not authorized for the ${roleLabel} Portal. ${roleLabel} accounts must be added by an Administrator in Team Access before logging in.`,
      );
    }
  }

  useEffect(() => {
    if (!authorized) {
      track("page_view", { appName, pagePath: "/login" });
    }
  }, [authorized, appName]);

  if (!authorized) {
    return (
      <LoginScreen
        appName={appName}
        subtitle={subtitle}
        roleLabel={roleLabel}
        onSendOTP={async (phoneNumber) => {
          // Validate portal authorization BEFORE sending SMS OTP
          await resolvePortalAccess(phoneNumber);

          const res = await sendFirebasePhoneOTP(phoneNumber);
          setConfirmationResult(res.confirmationResult);
          toast({
            title: "Verification OTP Sent 📱",
            description: `6-digit OTP code sent via SMS to +91 ${phoneNumber}.`,
            variant: "info",
          });
          return { isDemoMode: res.isDemoMode };
        }}
        onVerifyOTP={async (otpCode) => {
          return verifyFirebasePhoneOTP(confirmationResult, otpCode);
        }}
        onSubmit={async (values) => {
          if (values.isSignUp) {
            if (!isRetailerPortal) {
              throw new Error(
                `Access Denied: Self-registration is disabled for the ${roleLabel} Portal.`,
              );
            }
            // Prevent Admin/Operations phone numbers from signing up as a Retailer
            const existingAccess = await resolvePortalAccess(values.phone || values.userId);
            if (existingAccess) {
              throw new Error(
                `Phone number ${values.phone || values.userId} is already registered to "${existingAccess.businessName || existingAccess.fullName}". Please sign in with OTP instead.`,
              );
            }

            const cleanSignupPhone = normalizePhone(values.phone || values.userId);
            if (cleanSignupPhone.length < 10) {
              throw new Error("Please enter a valid 10-digit mobile number.");
            }

            // Register new Retailer account
            const newSession = register({
              userId: cleanSignupPhone,
              passwordHash: "otp_verified",
              fullName: values.fullName || cleanSignupPhone,
              roles: ["RETAILER"],
              phone: cleanSignupPhone,
              phoneNumber: cleanSignupPhone,
              orgId: `retailer_${cleanSignupPhone}`,
              organizationId: `retailer_${cleanSignupPhone}`,
              businessName: values.businessName || "Kirana Store",
              accountType: values.accountType || roleLabel,
              city: values.city || "Bangalore",
              gstNumber: values.gstNumber || undefined,
            });

            toast({
              title: "Phone Verified & Account Created! 🎉",
              description: `Welcome ${newSession.fullName}. Your shop profile has been initialized.`,
              variant: "success",
            });

            try {
              const createdRetailer = await client.retailers.create({
                id: null as any,
                name: values.businessName || values.fullName || "Kirana Store",
                phone: cleanSignupPhone,
                city: values.city || "Bangalore",
                addressLine: values.addressLine || ("Main Market Lane, " + (values.city || "Bangalore")),
                latitude: values.latitude ?? null,
                longitude: values.longitude ?? null,
                gstNumber: values.gstNumber || undefined,
                photoUrl: values.photoUrl || "https://images.unsplash.com/photo-1578916171728-46686eac8d58?w=500&q=80",
                status: "PENDING",
                onboardingSource: "SELF_SIGNUP",
                onboardedBy: "Self Signup",
              } as any);
              await client.users.create({
                id: null,
                username: cleanSignupPhone,
                fullName: values.fullName || values.businessName || cleanSignupPhone,
                role: "RETAILER",
                retailerId: createdRetailer.id ?? null,
                phone: cleanSignupPhone,
              });
            } catch {
              // Non-blocking catch if offline
            }
          } else {
            // Sign In flow — strictly verified against backend PostgreSQL role
            const lookupKey = values.phone || values.userId;
            const existingUser = await resolvePortalAccess(lookupKey);

            if (!existingUser) {
              if (isRetailerPortal) {
                // Open frictionless login: auto-create basic buyer record & log them directly into catalog
                const cleanDigits = normalizePhone(lookupKey);
                const storeName = `Store ${cleanDigits.slice(-4)}`;
                const newSession = register({
                  userId: cleanDigits || lookupKey,
                  passwordHash: "otp_verified",
                  fullName: storeName,
                  roles: ["RETAILER"],
                  phone: cleanDigits ? `+91 ${cleanDigits}` : lookupKey,
                  phoneNumber: cleanDigits ? `+91 ${cleanDigits}` : lookupKey,
                  orgId: `retailer_${cleanDigits || lookupKey}`,
                  organizationId: `retailer_${cleanDigits || lookupKey}`,
                  businessName: storeName,
                  accountType: roleLabel,
                  city: "Bengaluru",
                });

                try {
                  const created = await client.retailers.create({
                    id: null as any,
                    name: storeName,
                    phone: cleanDigits || lookupKey,
                    city: "Bengaluru",
                    addressLine: "Bengaluru",
                    latitude: null,
                    longitude: null,
                    status: "PENDING",
                    photoUrl: null,
                    onboardingSource: "SELF_SIGNUP",
                    onboardedBy: "Self Signup",
                  } as any);

                  await client.users.create({
                    id: null,
                    username: cleanDigits || lookupKey,
                    fullName: storeName,
                    role: "RETAILER",
                    retailerId: created.id ?? null,
                    phone: cleanDigits || lookupKey,
                  });
                } catch {
                  // Non-blocking
                }

                track("sign_up", { appName, userId: newSession.userId, propsJson: { role: roleLabel } });
                login(newSession);
                toast({
                  title: "Welcome! 👋",
                  description: "You're logged in. Explore live wholesale staples prices.",
                  variant: "success",
                });
                return;
              }
              throw new Error(
                `Access Denied: Mobile number ${values.phone || values.userId} is not registered. ${roleLabel} accounts must be authorized by an Administrator before logging in.`,
              );
            }

            const hasRequiredRole = existingUser.roles.some(
              (r) => r === "ADMIN" || r === "SUPER_ADMIN" || roles.includes(r),
            );
            if (!hasRequiredRole) {
              throw new Error(
                `Access Denied: Account ${existingUser.fullName} (${existingUser.userId}) is not authorized to access the ${roleLabel} Portal.`,
              );
            }

            const userPhone = existingUser.phone || existingUser.phoneNumber || values.phone;
            const isCoFounder =
              isSuperAdminPhone(userPhone) || isSuperAdminPhone(existingUser.userId);
            const effectiveSessionRoles: RoleType[] = isCoFounder
              ? [...SUPER_ADMIN_ROLES]
              : existingUser.roles.includes("ADMIN")
              ? [...ALL_ROLES]
              : existingUser.roles;

            const userOrgId =
              existingUser.orgId ||
              existingUser.organizationId ||
              (existingUser.roles.includes("ADMIN") || isCoFounder ? "org_mdd_admin" : "mydailydirect");

            const nextSession: Session = {
              tenantId: values.tenantId,
              userId: existingUser.userId,
              fullName: existingUser.fullName,
              roles: effectiveSessionRoles,
              phone: userPhone,
              phoneNumber: userPhone,
              orgId: userOrgId,
              organizationId: userOrgId,
              businessName: existingUser.businessName || values.businessName,
              accountType: existingUser.accountType || roleLabel,
            };

            track("login", { appName, userId: nextSession.userId, propsJson: { role: roleLabel } });
            login(nextSession);

            toast({
              title: "Phone Verification Successful",
              description: `Authenticated as ${nextSession.fullName} (${roleLabel}).`,
              variant: "success",
            });
          }
        }}
      />
    );
  }

  return <>{children}</>;
}

/**
 * The single composition root every Trado app mounts.
 */
export function AppRoot(props: AppRootProps) {
  const queryClient = useMemo(makeQueryClient, []);

  // Initialize portal design direction token and font
  useEffect(() => {
    const urlOverride = readDesignDirection();
    const effectiveDir = urlOverride ?? props.designDirection ?? null;
    if (effectiveDir) {
      applyDesignDirection(effectiveDir);
    }
  }, [props.designDirection]);

  // Unregister stale service workers and clear Workbox caches so security & auth updates apply immediately
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          void registration.unregister();
        }
      });
    }
    if (typeof window !== "undefined" && "caches" in window) {
      window.caches.keys().then((keys) => {
        for (const key of keys) {
          void window.caches.delete(key);
        }
      });
    }
  }, []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <LoginGate {...props} />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
